import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ProgramContext = createContext();

export const useProgram = () => {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error('useProgram must be used within a ProgramProvider');
  }
  return context;
};

export const ProgramProvider = ({ children }) => {
  const [programs, setPrograms] = useState([]);
  const [learningPaths] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProgramData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProgramData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/programs');
      if (data.success) {
        setPrograms(data.programs || []);
      }
    } catch (err) {
      console.error('[ProgramContext] Erreur chargement:', err);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const getProgramById = (programId) => {
    return programs.find(program => program.id === programId);
  };

  const getLearningPathById = (pathId) => {
    return learningPaths.find(path => path.id === pathId);
  };

  const getUserProgress = (programId) => {
    return userProgress[programId] || {
      enrolled: false,
      progress: 0,
      completedCourses: [],
      currentStep: 1
    };
  };

  const enrollInProgram = (programId) => {
    setUserProgress(prev => ({
      ...prev,
      [programId]: {
        ...prev[programId],
        enrolled: true,
        startDate: new Date().toISOString().split('T')[0],
        progress: 0,
        completedCourses: [],
        currentStep: 1
      }
    }));
  };

  const completeCourse = (programId, courseId) => {
    setUserProgress(prev => {
      const currentProgress = prev[programId];
      if (!currentProgress) return prev;

      const updatedCompletedCourses = [...currentProgress.completedCourses, courseId];
      const program = getProgramById(programId);
      const progressPercentage = program?.courses?.length
        ? (updatedCompletedCourses.length / program.courses.length) * 100
        : 0;

      return {
        ...prev,
        [programId]: {
          ...currentProgress,
          completedCourses: updatedCompletedCourses,
          progress: Math.round(progressPercentage)
        }
      };
    });
  };

  const getProgramsByCategory = (category) => {
    return programs.filter(program => program.category === category);
  };

  const getRecommendedPrograms = () => {
    return programs.slice(0, 3);
  };

  const value = {
    programs,
    learningPaths,
    userProgress,
    loading,
    getProgramById,
    getLearningPathById,
    getUserProgress,
    enrollInProgram,
    completeCourse,
    getProgramsByCategory,
    getRecommendedPrograms
  };

  return (
    <ProgramContext.Provider value={value}>
      {children}
    </ProgramContext.Provider>
  );
};
